import { useContext, useEffect, useState } from 'react';
import { Button, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { FcFinePrint } from 'react-icons/fc';
import { toast } from "react-toastify";
import { LoaderContext } from '../../../App';
import GHGEmissionService from '../../../core/service/GHGEmissionService';
import Nodataimg from "../../../images/Assessment.png";
import './UAEFDL.scss';


const UAEFDLIdentifyEmissionSource = ({ t, filterData, nextPage, prevPage, setHasUnsavedData, registerSaveHandler, gotoPage }) => {
    const data = JSON.parse(localStorage.getItem("authority"));
    const companyId = data.profile.company.id;
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [sourceDisplay, setSourceDisplay] = useState({
        'Scope 1': {},
        'Scope 2': {},
        'Scope 3': {}
    });
    const [scopeData, setScopeData] = useState({});
    const [selectedSources, setSelectedSources] = useState([]);
    const [hasAnswered, setHasAnswered] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [hasUnsavedData, setLocalHasUnsavedData] = useState(false);
    const [searchTerms, setSearchTerms] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadEmissionData();
    }, [filterData]);

    useEffect(() => {
        if (registerSaveHandler) {
            registerSaveHandler((nav) => saveEmissionSources(nav));
        }
    }, [selectedSources, filterData]);

    const setInitialItemActive = () => {
        if (!scopeData || Object.keys(scopeData).length === 0) {
            return;
        }

        const initialState = {};
        Object.keys(scopeData).forEach((scope) => {
            if (scopeData[scope] && Object.keys(scopeData[scope]).length > 0) {
                initialState[scope] = {};
                const firstCategory = Object.keys(scopeData[scope])[0];
                Object.keys(scopeData[scope]).forEach((category) => {
                    initialState[scope][category] = (category === firstCategory);
                });
            }
        });
        setSourceDisplay(initialState);
    }

    const getAllSourcesWithCo2 = () => {
        if (!scopeData || Object.keys(scopeData).length === 0) {
            return [];
        }

        const sources = [];
        Object.values(scopeData).forEach(scope => {
            Object.values(scope).forEach(category => {
                category.forEach(source => {
                    if (source.factors.some(f => f.ghg === 'CO2')) {
                        sources.push(source.sourceId);
                    }
                });
            });
        });
        return sources;
    };

    useEffect(() => {
        if (scopeData && Object.keys(scopeData).length > 0) {
            setInitialItemActive();
        }
    }, [scopeData]);

    useEffect(() => {
        if (!scopeData || Object.keys(scopeData).length === 0) {
            return;
        }

        const initialSelected = [];
        const allSourcesWithCo2 = getAllSourcesWithCo2();
        Object.values(scopeData).forEach(scope => {
            Object.values(scope).forEach(category => {
                category.forEach(source => {
                    if (source.alreadySelected) {
                        const co2Factor = source.factors.find(
                            f => f.ghg === 'CO2'
                        );
                        if (co2Factor && allSourcesWithCo2.includes(source.sourceId)) {
                            initialSelected.push({
                                emissionSourceId: source.sourceId,
                                emissionFactors: [{ emissionFactorId: co2Factor.id }]
                            });
                        }
                    }
                });
            });
        });
        setSelectedSources(initialSelected);
        setSelectAll(initialSelected.length === allSourcesWithCo2.length);
    }, [scopeData]);

    const hasAnsweredQuestions = (data) => {
        for (const scope of data) {
            for (const category of scope.categories) {
                for (const question of category.questions) {
                    if (question.answer === "Yes" || question.answer === "No") {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    const loadEmissionData = async () => {
        setIsLoading(true);
        setLoaderDisplay(true);
        const date = filterData?.year + '-01-01';
        const emissionTable = filterData.emissionTable ?? 'GHG';

        try {
            const [emissionSourcesResponse, summaryResponse] = await Promise.all([
                GHGEmissionService.getEmissionSourcesData(emissionTable, companyId, date),
                GHGEmissionService.getSummaryData(emissionTable, companyId, date)
            ]);

            // Process emission sources data
            if (emissionSourcesResponse?.data?.data) {
                setScopeData(emissionSourcesResponse.data.data);
                setLocalHasUnsavedData(false);
                setHasUnsavedData(false);
                setIsProcessing(false);
            }

            // Process summary data
            if (summaryResponse?.data?.data) {
                setHasAnswered(hasAnsweredQuestions(summaryResponse.data.data['operationalBoundary']));
            }
        } catch (error) {
            console.error('Error loading emission data:', error);
            setIsProcessing(false);
            // Set default values on error
            setScopeData({});
            setHasAnswered(false);
        } finally {
            setLoaderDisplay(false);
            setIsLoading(false);
        }
    }

    const saveEmissionSources = async (nav) => {
        if (selectedSources.length == 0) {
            toast.warning(t('SelectAtLeastOneBeforeNextStep.label'), {
                position: toast.POSITION.TOP_RIGHT,
            });
            return false;
        } else {
            setLoaderDisplay(true);
            setIsProcessing(true);
            const date = filterData?.year + '-01-01';
            try {
                const response = await GHGEmissionService.saveEmissionSources(filterData.emissionTable ?? 'GHG', companyId, date, prepareApiData());
                setLoaderDisplay(false);
                setIsProcessing(false);
                if (response.status !== 200) {
                    return false;
                }
                toast.success(t('EmissionSourcesSavedSuccess.label'), {
                    position: toast.POSITION.TOP_RIGHT,
                });
                setLocalHasUnsavedData(false);
                setHasUnsavedData(false);
                if (nav == 'next') {
                    setTimeout(() => {
                        nextPage('Review');
                    }, 500);
                } else if (nav == 'back') {
                    setTimeout(() => {
                        prevPage('Opr');
                    }, 500);
                }
                return true;
            } catch (error) {
                setLoaderDisplay(false);
                setIsProcessing(false);
                return false;
            }
        }
    }

    const renderEmptyDataLabel = () => {
        return (
            <tr className="Nodatafound_box">
                <td colSpan={4} className='text-center'><FcFinePrint className="me-2" />{t("Nodatafound.label")}</td>
            </tr>
        )
    };

    const getCo2Factor = (sourceId) => {
        for (const scope of Object.values(scopeData)) {
            for (const category of Object.values(scope)) {
                const source = category.find(s => s.sourceId === sourceId);
                if (source) {
                    return source.factors.find(
                        f => f.ghg === 'CO2'
                    );
                }
            }
        }
        return null;
    };

    const toggleSourceSelection = (sourceId) => {
        setSelectedSources(prev => {
            const existingIndex = prev.findIndex(
                item => item.emissionSourceId === sourceId
            );

            if (existingIndex >= 0) {
                return prev.filter(item => item.emissionSourceId !== sourceId);
            } else {
                const co2Factor = getCo2Factor(sourceId);
                if (!co2Factor) return prev;

                return [
                    ...prev,
                    {
                        emissionSourceId: sourceId,
                        emissionFactors: [{ emissionFactorId: co2Factor.id }]
                    }
                ];
            }
        });
        setLocalHasUnsavedData(true);
        setHasUnsavedData(true);
    };

    const toggleScopeSelection = (scopeName) => {
        const scopeSources = [];
        Object.values(scopeData[scopeName]).forEach(category => {
            category.forEach(source => {
                const co2Factor = source.factors.find(
                    f => f.ghg === 'CO2'
                );
                if (co2Factor) {
                    scopeSources.push({
                        emissionSourceId: source.sourceId,
                        emissionFactors: [{ emissionFactorId: co2Factor.id }]
                    });
                }
            });
        });

        setSelectedSources(prev => {
            const currentScopeSources = prev.filter(item =>
                scopeSources.some(s => s.emissionSourceId === item.emissionSourceId)
            );

            if (currentScopeSources.length === scopeSources.length) {
                return prev.filter(item =>
                    !scopeSources.some(s => s.emissionSourceId === item.emissionSourceId)
                );
            } else {
                const newSelection = prev.filter(item =>
                    !scopeSources.some(s => s.emissionSourceId === item.emissionSourceId)
                );
                return [...newSelection, ...scopeSources];
            }
        });
        setLocalHasUnsavedData(true);
        setHasUnsavedData(true);
    };

    const toggleSelectAll = () => {
        const allSourcesWithCo2 = getAllSourcesWithCo2();
        if (selectAll) {
            setSelectedSources([]);
        } else {
            const newSelection = [];
            Object.values(scopeData).forEach(scope => {
                Object.values(scope).forEach(category => {
                    category.forEach(source => {
                        const co2Factor = source.factors.find(
                            f => f.ghg === 'CO2'
                        );
                        if (co2Factor) {
                            newSelection.push({
                                emissionSourceId: source.sourceId,
                                emissionFactors: [{ emissionFactorId: co2Factor.id }]
                            });
                        }
                    });
                });
            });
            setSelectedSources(newSelection);
        }
        setSelectAll(!selectAll);
        setLocalHasUnsavedData(true);
        setHasUnsavedData(true);
    };


    const toggleCategorySelection = (scopeName, categoryName) => {
        const categorySources = scopeData[scopeName][categoryName]
            .filter(source => source.factors.some(f => f.ghg === 'CO2'))
            .map(source => ({
                emissionSourceId: source.sourceId,
                emissionFactors: [{
                    emissionFactorId: source.factors.find(
                        f => f.ghg === 'CO2'
                    ).id
                }]
            }));

        setSelectedSources(prev => {

            const allSelected = categorySources.every(catSource =>
                prev.some(selSource =>
                    selSource.emissionSourceId === catSource.emissionSourceId
                )
            );

            if (allSelected) {

                return prev.filter(selSource =>
                    !categorySources.some(catSource =>
                        catSource.emissionSourceId === selSource.emissionSourceId
                    )
                );
            } else {

                const newSelection = prev.filter(selSource =>
                    !categorySources.some(catSource =>
                        catSource.emissionSourceId === selSource.emissionSourceId
                    )
                );
                return [...newSelection, ...categorySources];
            }
        });
        setLocalHasUnsavedData(true);
        setHasUnsavedData(true);
    };


    const isCategorySelected = (scopeName, categoryName) => {
        const category = scopeData[scopeName][categoryName];
        if (!category) return false;

        const categorySourcesWithCo2 = category.filter(source =>
            source.factors.some(f => f.ghg === 'CO2')
        );

        return categorySourcesWithCo2.length > 0 &&
            categorySourcesWithCo2.every(source =>
                selectedSources.some(item => item.emissionSourceId === source.sourceId)
            );
    };

    const isScopeSelected = (scopeName) => {
        const scopeSources = [];
        Object.values(scopeData[scopeName]).forEach(category => {
            category.forEach(source => {
                if (source.factors.some(f => f.ghg === 'CO2')) {
                    scopeSources.push(source.sourceId);
                }
            });
        });

        return scopeSources.length > 0 &&
            scopeSources.every(id =>
                selectedSources.some(item => item.emissionSourceId === id)
            );
    };

    const isSourceSelected = (sourceId) => {
        return selectedSources.some(item => item.emissionSourceId === sourceId);
    };

    const prepareApiData = () => {
        return selectedSources.map((source, index) => ({
            // id: index + 1,
            ...source
        }));
    };

    const nonEmptyScopes = ['Scope 1', 'Scope 2', 'Scope 3'].filter(
        scope => scopeData[scope] && Object.keys(scopeData[scope]).length > 0
    );

    const handleSearchChange = (scope, category, term) => {
        setSearchTerms(prev => ({
            ...prev,
            [`${scope}-${category}`]: term.toLowerCase()
        }));
    };

    const handleItemClick = (scope, category) => {
        const newSourceDisplay = JSON.parse(JSON.stringify(sourceDisplay));

        Object.keys(newSourceDisplay[scope]).forEach((key) => {
            newSourceDisplay[scope][key] = false;
        });

        newSourceDisplay[scope][category] = !newSourceDisplay[scope][category];

        setSourceDisplay(newSourceDisplay);
    };
   

    return (
        <>
            {isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <Spinner animation="border" role="status" className="mb-3">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="text-muted">{t('LoadingEmissionSourcesData.label')}</p>
                    </div>
                </div>
            ) : (
                <>
                    {
                        hasAnswered && Object.keys(scopeData).length > 0 &&
                        <>
                            <div className='IdentifyEmissionSourceCard'>
                                <div className='IdentifyEmissionSourceRow row'>
                                    <div className='col-lg-10 p-0'>
                                        <div className='mb-2 text-center'>{t('IdentifyDescription.label')}</div>
                                        <div className='card mt-3'>

                                            <Tabs defaultActiveKey={nonEmptyScopes[0]} id="scope-tabs">
                                                {nonEmptyScopes.map(scope => (
                                                    <Tab key={scope} eventKey={scope}
                                                        title={
                                                            <>
                                                                {/* {allQuestionsAnswered(scope) && <BsCheckCircle className='me-2' />} */}
                                                                {scope}
                                                            </>
                                                        }>

                                                        <div>
                                                            <div className="row m-0">
                                                                <div className="col-md-3 ScopeSub_topicCol p-0">
                                                                    <ul className='ScopeSub_topic'>
                                                                        {Object.keys(scopeData[scope]).map(category => (
                                                                            <li className={`${sourceDisplay[scope] && sourceDisplay[scope][category] === true ? 'active' : ''}`}
                                                                                onClick={() => { handleItemClick(scope, category); handleSearchChange(scope, category, '') }}>
                                                                                {<h6 className="mb-0">{category}</h6>}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                                <div className="col-md-9 p-3">
                                                                    {Object.keys(scopeData[scope]).map(category => (
                                                                        <>
                                                                            {
                                                                                sourceDisplay[scope] && sourceDisplay[scope][category] === true &&
                                                                                <>
                                                                                    <div className='p-2 w-50 ScopeSub_topicListSearch'>
                                                                                        <input
                                                                                            type="text"
                                                                                            placeholder={t('SearchEmissionSources.label')}
                                                                                            className="form-control"
                                                                                            onChange={(e) => handleSearchChange(scope, category, e.target.value)}
                                                                                        />
                                                                                    </div>
                                                                                    <div className='card ScopeSub_Listtable' style={{ maxHeight: 'calc(100vh - 418px)', overflow: 'auto' }}>
                                                                                        <Table hover className='mb-0 table-border-bottom-0'>
                                                                                            <thead className="sticky-top">
                                                                                                <tr>
                                                                                                    <th>
                                                                                                        <input
                                                                                                            type="checkbox"
                                                                                                            className='form-check-input'
                                                                                                            checked={isCategorySelected(scope, category)}
                                                                                                            onChange={() => toggleCategorySelection(scope, category)}
                                                                                                        />
                                                                                                    </th>
                                                                                                    <th width="30%">{t('EmissionSource.label')}</th>
                                                                                                    <th width="15%" className='text-center'>{t('CO2Factor.label')}</th>
                                                                                                    <th width="20%" className='text-center'>{t('Unit.label')}</th>
                                                                                                    <th width="30%">{t('Notes.label')}</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {scopeData[scope][category].filter(source => {
                                                                                                    const searchTerm = searchTerms[`${scope}-${category}`];
                                                                                                    const hasValidEfValue = source.factors?.some(factor => factor.ghg == 'CO2' && factor.efValue != null);
                                                                                                    return hasValidEfValue && (!searchTerm ||
                                                                                                        source.name.toLowerCase().includes(searchTerm));
                                                                                                }).map(source => (
                                                                                                    <tr key={source.sourceId}>
                                                                                                        <td className='checkbox'>
                                                                                                            <input
                                                                                                                type="checkbox"
                                                                                                                className='form-check-input'
                                                                                                                checked={isSourceSelected(source.sourceId)}
                                                                                                                onChange={() => toggleSourceSelection(source.sourceId)}
                                                                                                            />
                                                                                                        </td>
                                                                                                        <td>{source.name}</td>
                                                                                                        <td className='text-center'>
                                                                                                            {source.factors
                                                                                                                .filter(factor => (factor.ghg == 'CO2' && factor.efValue != null))
                                                                                                                .map(factor => (factor.efValue))
                                                                                                            }
                                                                                                        </td>
                                                                                                        <td className='text-center'>
                                                                                                            {source.factors
                                                                                                                .filter(factor => (factor.ghg == 'CO2' && factor.efValue != null))
                                                                                                                .map(factor => (factor.unit))
                                                                                                            }
                                                                                                        </td>
                                                                                                        <td>
                                                                                                            {source.factors
                                                                                                                .filter(factor => (factor.ghg == 'CO2' && factor.efValue != null))
                                                                                                                .map(factor => (factor.notes))
                                                                                                            }
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    </div>
                                                                                </>
                                                                            }
                                                                        </>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </Tab>
                                                ))}
                                            </Tabs>
                                        </div>
                                        <div className='text-center p-2 GHGInventory_cardFooter pt-3'>
                                            <Button variant='secondary' className='px-4' onClick={() => saveEmissionSources('back')} disabled={isProcessing}>
                                                {t('Back.label')}
                                            </Button>
                                            {/* <Button variant='primary' className='px-4 mx-2' onClick={() => saveEmissionSources()} disabled={isProcessing}>
                                                {isProcessing ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                                                {t('Save.label')}
                                            </Button> */}
                                            <Button variant='secondary' className='px-4 mx-2' onClick={() => saveEmissionSources('next')} disabled={isProcessing}>
                                                {t('Next.label')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    }
                    {
                        !hasAnswered &&
                        <div className='col-lg-12 px-0 py-2 text-center'>
                            <div className='noDataCard'>
                                <div>
                                    <div className='Nodataimg_box'>
                                        <img src={Nodataimg} />
                                    </div>
                                    <h5 className='py-3'>{t('ForIdentifyingEmissionSource.label')}</h5>
                                    <Button variant="primary" className="px-3 mx-2" onClick={() => prevPage('Opr')}>
                                        {t('GoToStep2.label')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    }
                    {/* {
                        Object.keys(scopeData).length == 0 &&
                        <div className='col-lg-12 px-0 py-2 text-center'>
                            <div className='noDataCard'>
                                <div>
                                    <div className='Nodataimg_box'>
                                        <img src={Nodataimg} />
                                    </div>
                                    <h5 className='py-3'>These is no emission sources available for the selected items in Step 2.</h5>
                                    <Button variant="primary" className="px-3 mx-2" onClick={() => prevPage('Opr')}>
                                        Go to Step 2
                                    </Button>
                                </div>
                            </div>
                        </div>
                    } */}
                </>
            )}
        </>
    );
}
export default withTranslation()(UAEFDLIdentifyEmissionSource);